import { useRef } from 'react';
import { useParams } from 'react-router-dom';
import BrandLogo from '../BrandLogo';
import UserMenu from '../UserMenu';
import RunSimulationButton from '../simulation/RunSimulationButton';
import EditableProjectTitle from './EditableProjectTitle';
import EditorViewMenu from './EditorViewMenu';
import { useEditorStore } from '../../store/editorStore';
import type { ProjectDocument } from '../../types/project';

interface TopBarProps {
  onSave: () => void;
  onCloseProject: () => void;
  onRenameProject: (name: string) => Promise<void>;
  isDirty: boolean;
  isSaving: boolean;
  error: string | null;
  readOnly?: boolean;
}

function ArrowLeftIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-4 w-4 shrink-0"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function UndoIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-4 w-4 shrink-0"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        d="M7.793 2.232a.75.75 0 0 1 1.06 0l3.25 3.25a.75.75 0 0 1-1.06 1.06L9.5 4.439V10a5.5 5.5 0 0 0 5.5 5.5h1.25a.75.75 0 0 1 0 1.5H15a7 7 0 0 1-7-7V4.439L4.603 6.542a.75.75 0 0 1-1.06-1.06l3.25-3.25Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function RedoIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-4 w-4 shrink-0"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        d="M12.207 2.232a.75.75 0 0 0-1.06 0l-3.25 3.25a.75.75 0 0 0 1.06 1.06L10.5 4.439V10a5.5 5.5 0 0 1-5.5 5.5H3.75a.75.75 0 0 0 0 1.5H5a7 7 0 0 0 7-7V4.439l1.897 2.103a.75.75 0 0 0 1.06-1.06l-3.25-3.25Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export default function TopBar({
  onSave,
  onCloseProject,
  onRenameProject,
  isDirty,
  isSaving,
  error,
  readOnly = false,
}: TopBarProps) {
  const { id: projectId } = useParams<{ id: string }>();
  const document = useEditorStore((s) => s.document);
  const permission = useEditorStore((s) => s.permission);
  const importDocument = useEditorStore((s) => s.importDocument);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  const canUndo = useEditorStore((s) => s.historyPast.length > 0);
  const canRedo = useEditorStore((s) => s.historyFuture.length > 0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canRun = permission === 'run' || permission === 'edit';

  const projectName = document?.name ?? 'Untitled';

  const handleExport = () => {
    if (!document) return;
    const blob = new Blob([JSON.stringify(document, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = `${projectName.replace(/\s+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string) as ProjectDocument;
        if (!parsed.nodes || !parsed.routes) {
          throw new Error('Invalid project file');
        }
        importDocument({
          ...parsed,
          name: parsed.name || projectName,
        });
      } catch {
        alert('Could not import file. Please select a valid project JSON export.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const saveStatus = readOnly ? (
    <span className="status-pill bg-surface-overlay text-text-muted">
      Read-only
    </span>
  ) : isSaving ? (
    <span className="status-pill-saving">
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-text-muted" />
      Saving…
    </span>
  ) : isDirty ? (
    <span className="status-pill-dirty">Unsaved</span>
  ) : (
    <span className="status-pill-saved">Saved</span>
  );

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-surface-raised px-4 shadow-sm">
      <div className="flex min-w-0 items-center gap-3 sm:gap-4">
        <button
          type="button"
          onClick={onCloseProject}
          className="btn-secondary shrink-0 px-2.5 py-1.5 text-xs sm:px-3 sm:text-sm"
          title="Return to project library"
        >
          <ArrowLeftIcon />
          <span className="hidden sm:inline">Projects</span>
          <span className="sm:hidden">Back</span>
        </button>
        <BrandLogo variant="mark" linkToHome onHomeClick={onCloseProject} />
        <div className="hidden h-6 w-px bg-border sm:block" />
        <div className="min-w-0">
          <EditableProjectTitle
            name={projectName}
            readOnly={readOnly}
            onRename={onRenameProject}
          />
          <p className="text-[11px] text-text-subtle">Simulation Designer</p>
        </div>
        <div className="ml-2 hidden sm:block">{saveStatus}</div>
        <EditorViewMenu />
      </div>

      <div className="flex items-center gap-2">
        {error && (
          <span
            className="max-w-[12rem] truncate text-xs text-danger"
            title={error}
          >
            {error}
          </span>
        )}
        {!readOnly && (
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={handleImportFile}
          />
        )}
        {!readOnly && (
          <div className="hidden items-center gap-0.5 sm:flex">
            <button
              type="button"
              onClick={undo}
              disabled={!canUndo}
              className="btn-ghost px-2 py-1.5 text-xs disabled:opacity-40"
              title="Undo (Ctrl+Z)"
              aria-label="Undo"
            >
              <UndoIcon />
            </button>
            <button
              type="button"
              onClick={redo}
              disabled={!canRedo}
              className="btn-ghost px-2 py-1.5 text-xs disabled:opacity-40"
              title="Redo (Ctrl+Y)"
              aria-label="Redo"
            >
              <RedoIcon />
            </button>
          </div>
        )}
        {!readOnly && (
          <button
            type="button"
            onClick={handleImportClick}
            className="btn-ghost hidden px-3 py-1.5 text-xs sm:inline-flex"
            title="Import project JSON"
          >
            Import
          </button>
        )}
        <button
          type="button"
          onClick={handleExport}
          disabled={!document}
          className="btn-ghost hidden px-3 py-1.5 text-xs sm:inline-flex"
          title="Export project JSON"
        >
          Export
        </button>
        {canRun && (
          <RunSimulationButton
            projectId={projectId}
            size="compact"
            className="hidden md:inline-flex"
          />
        )}
        {!readOnly && (
          <button
            type="button"
            onClick={onSave}
            disabled={!isDirty || isSaving}
            className="btn-primary px-4 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        )}
        <UserMenu />
      </div>
    </header>
  );
}
